'use strict'

import React, { useCallback, useMemo } from 'react'

import { ChrofyShowErrors } from '../../../common/Form'
import { ChrofyCustomSelect } from '../../../common/ChrofyCustomSelect'
import { getMessage } from '../../../../common/utils/MessageUtils'
import { astCol, astLiteral } from '../extra/conditionExpressionAst'
import {
  CONDITION_MAPPING_DATE_FORMAT_ERROR,
  CONDITION_MAPPING_DATE_PLACEHOLDER,
  isValidConditionMappingDateValue
} from '../extra/validation'

const COMPARE_VALUE_TYPES = [
  { value: 'fixed', label: '固定値' },
  { value: 'item', label: '項目' },
  { value: 'date', label: '日付' },
  { value: 'empty', label: '(空)' }
]
const RESULT_VALUE_TYPES = [
  { value: 'fixed', label: '固定値' },
  { value: 'item', label: '項目' },
  { value: 'date', label: '日付' },
  { value: 'empty', label: '(空)' }
]
const OPERATORS = [
  { value: '==', label: '等しい' },
  { value: '!=', label: '等しくない' },
  { value: '>', label: 'より大きい' },
  { value: '>=', label: '以上' },
  { value: '<', label: 'より小さい' },
  { value: '<=', label: '以下' }
]
const ERRORS = {
  AT_LEAST_ONE_CONDITION: getMessage('E011017', '条件'),
  COMPARE_COLUMN_REQUIRED: getMessage('E010017', '比較項目（ファイル種別・項目名）'),
  FILE_TYPE_REQUIRED: getMessage('E010017', 'ファイル種別'),
  COLUMN_NAME_REQUIRED: getMessage('E010017', '項目名'),
  OPERATOR_REQUIRED: getMessage('E010017', '演算子'),
  COMPARE_VALUE_REQUIRED: getMessage('E010001', '比較値'),
  COMPARE_DATE_FORMAT: getMessage('E010002', '比較値（日付）'),
  COMPARE_ITEM_REQUIRED: getMessage('E010017', '比較項目'),
  RESULT_VALUE_REQUIRED: getMessage('E010001', '結果値'),
  RESULT_DATE_FORMAT: getMessage('E010002', '結果値（日付）'),
  RESULT_ITEM_REQUIRED: getMessage('E010017', '結果項目'),
  ELSE_VALUE_REQUIRED: getMessage('E010001', 'Elseの結果値'),
  ELSE_ITEM_REQUIRED: getMessage('E010017', 'Elseの結果項目'),
  ELSE_DATE_FORMAT: CONDITION_MAPPING_DATE_FORMAT_ERROR,
  AST_BUILD_FAILED: getMessage('E011004', '条件の組み立て')
}

/**
 * 条件行の結果値（日付）を AST 用 Literal にする
 *
 * 入力形式（`YYYY/MM/DD` / `YYYY-MM-DD`）を正規化せずそのまま保存する。 CHROFY 標準形式でない値は検証（プレビュー）でエラーとして検出させるため。
 *
 * @param {string} raw
 * @returns {object}
 */
function conditionResultDateLiteralForAst(raw) {
  return astLiteral(String(raw ?? '').trim())
}

/**
 * Else 日付を CHROFY 標準形式へ正規化して AST 用 Literal を返す
 *
 * @param {string} raw
 * @returns {object}
 */
function elseDateLiteralForAst(raw) {
  return astLiteral(String(raw ?? '').trim())
}

let nextId = 1
function generateRowId() {
  return `row-${nextId++}`
}

function createEmptyConditionRow() {
  return {
    id: generateRowId(),
    fileType: '',
    columnName: '',
    operator: '==',
    compareValueType: 'fixed',
    compareValue: '',
    compareItemFileType: '',
    compareItemColumnName: '',
    resultType: 'fixed',
    resultValue: '',
    resultItemFileType: '',
    resultItemColumnName: ''
  }
}

/**
 * AST を保存用 JSON 文字列に変換する
 *
 * @param {unknown} node
 * @returns {string}
 */
function astToStoredString(node) {
  return JSON.stringify(node)
}

/**
 * 保存済みの条件式/値を AST に復元する
 *
 * @param {unknown} stored
 * @returns {any}
 */
function storedStringToAst(stored) {
  if (stored?.type) return stored
  if (stored === 'true') return astLiteral(true)
  if (typeof stored !== 'string' || !stored.trim()) return null
  try {
    return JSON.parse(stored)
  } catch {
    return null
  }
}

/**
 * 1条件行から BinaryExpression + Value の AST を構築（SR-153）
 *
 * @param {{
 *   fileType: string
 *   columnName: string
 *   operator: string
 *   compareValueType: string
 *   compareValue: string
 *   compareItemFileType?: string
 *   compareItemColumnName?: string
 *   resultType: string
 *   resultValue: string
 *   resultItemFileType?: string
 *   resultItemColumnName?: string
 * }} row
 * @returns {{ condition: object; value: object } | null}
 */
function rowToAst(row) {
  if (!row.fileType?.trim() || !row.columnName?.trim() || !row.operator?.trim()) return null
  const left = astCol(row.fileType, row.columnName)
  let right
  if (row.compareValueType === 'item') {
    if (!row.compareItemFileType?.trim() || !row.compareItemColumnName?.trim()) return null
    right = astCol(row.compareItemFileType, row.compareItemColumnName)
  } else if (row.compareValueType === 'empty') {
    right = astLiteral('')
  } else {
    const v = String(row.compareValue ?? '').trim()
    if (!v) return null
    right = astLiteral(v)
  }
  const condition = {
    type: 'BinaryExpression',
    operator: row.operator,
    left,
    right
  }
  let value
  if (row.resultType === 'item') {
    if (!row.resultItemFileType?.trim() || !row.resultItemColumnName?.trim()) return null
    value = astCol(row.resultItemFileType, row.resultItemColumnName)
  } else if (row.resultType === 'empty') {
    value = astLiteral('')
  } else {
    const v = String(row.resultValue ?? '').trim()
    if (!v) return null
    value = row.resultType === 'date' ? conditionResultDateLiteralForAst(v) : astLiteral(v)
  }
  return { condition, value }
}

/**
 * ElseValueType と関連フィールドから else 分岐の Value AST を組み立てる
 *
 * @param {{
 *   elseValueType?: string
 *   elseValue?: string
 *   elseItemFileType?: string
 *   elseItemColumnName?: string
 * }} formData
 * @returns {object}
 */
function buildElseValueAst(formData) {
  const elseValueType = formData?.elseValueType ?? 'fixed'
  if (elseValueType === 'item') {
    return astCol(formData?.elseItemFileType ?? '', formData?.elseItemColumnName ?? '')
  }
  if (elseValueType === 'empty') {
    return astLiteral('')
  }
  if (elseValueType === 'date') {
    return elseDateLiteralForAst(formData?.elseValue ?? '')
  }
  return astLiteral(String(formData?.elseValue ?? '').trim())
}

/**
 * SingleCondition 配列を構築（SR-152）。条件成立時の値とは別に、末尾に { conditionalExpression: 'true', value: else 設定から組み立てた AST } を追加する
 *
 * @param {object[]} conditionRows
 * @param {{
 *   elseValueType?: string
 *   elseValue?: string
 *   elseItemFileType?: string
 *   elseItemColumnName?: string
 * }} [formData]
 * @returns {{ singleCondition: { conditionalExpression: string; value: string }[]; errors: Record<string, string[]> }}
 */
function buildSingleCondition(conditionRows, formData) {
  const errors = /** @type {Record<string, string[]>} */ ({})
  const singleCondition = []
  for (let i = 0; i < conditionRows.length; i++) {
    const row = conditionRows[i]
    const pair = rowToAst(row)
    if (!pair) {
      errors[`condition_${i}`] = [ERRORS.AST_BUILD_FAILED]
      continue
    }
    singleCondition.push({
      conditionalExpression: astToStoredString(pair.condition),
      value: astToStoredString(pair.value)
    })
  }
  if (singleCondition.length === 0 && Object.keys(errors).length === 0) {
    errors.general = [ERRORS.AT_LEAST_ONE_CONDITION]
  }
  if (singleCondition.length > 0) {
    singleCondition.push({
      conditionalExpression: astToStoredString(astLiteral(true)),
      value: astToStoredString(buildElseValueAst(formData ?? {}))
    })
  }
  return { singleCondition, errors }
}

/**
 * MappingInfo single_condition → formData (DB→UI)
 *
 * @param {{
 *   mappingType?: string
 *   formState?: {
 *     conditions?: object[]
 *     elseEnabled?: boolean
 *     elseValueType?: string
 *     elseValue?: string
 *     elseItemFileType?: string
 *     elseItemColumnName?: string
 *   }
 *   SingleCondition?: { condition?: object; value?: object }[]
 *   singleCondition?:
 *     | { conditionalExpression?: string; value?: string; formState?: unknown; form_state?: unknown }[]
 *     | { condition?: object; value?: object }[]
 * } | null} initial
 * @param {import('../extra/types').FileTypeInfo[]} fileTypes
 * @returns {{
 *   conditions: object[]
 *   elseEnabled: boolean
 *   elseValueType: string
 *   elseValue: string
 *   elseItemFileType: string
 *   elseItemColumnName: string
 * }}
 */
export function mappingInfoToFormData(initial, fileTypes) {
  const initialFormState =
    initial?.formState ??
    initial?.form_state ??
    (Array.isArray(initial?.singleCondition)
      ? (initial.singleCondition.find((row) => row?.formState || row?.form_state)?.formState ??
        initial.singleCondition.find((row) => row?.formState || row?.form_state)?.form_state)
      : null)
  if (initialFormState && typeof initialFormState === 'object') {
    return {
      conditions: Array.isArray(initialFormState.conditions) ? initialFormState.conditions : [],
      elseEnabled: Boolean(initialFormState.elseEnabled),
      elseValueType: String(initialFormState.elseValueType ?? 'fixed'),
      elseValue: String(initialFormState.elseValue ?? ''),
      elseItemFileType: String(initialFormState.elseItemFileType ?? ''),
      elseItemColumnName: String(initialFormState.elseItemColumnName ?? '')
    }
  }
  const raw = initial?.SingleCondition ?? initial?.singleCondition
  return singleConditionToForm(Array.isArray(raw) ? raw : [], fileTypes)
}

/**
 * FormData → MappingInfo single_condition (UI→DB)
 *
 * @param {{
 *   conditions: object[]
 *   elseEnabled: boolean
 *   elseValueType: string
 *   elseValue: string
 *   elseItemFileType?: string
 *   elseItemColumnName?: string
 * }} formData
 * @returns {{
 *   mappingType: 'SingleCondition'
 *   singleCondition: { conditionalExpression: string; value: string }[]
 *   formState: {
 *     conditions: object[]
 *     elseEnabled: boolean
 *     elseValueType: string
 *     elseValue: string
 *     elseItemFileType?: string
 *     elseItemColumnName?: string
 *   }
 * } | null}
 */
export function formDataToMappingInfo(formData) {
  const { singleCondition, errors: buildErrors } = buildSingleCondition(formData.conditions, formData)
  if (Object.keys(buildErrors).length > 0 || singleCondition.length === 0) {
    return null
  }
  return {
    mappingType: 'SingleCondition',
    singleCondition,
    formState: trimFormStateForStore(formData)
  }
}

/**
 * FormState 保存用に自由入力の文字列（比較値・結果値・Elseの結果値）をトリムする。 AST（singleCondition）側のトリムと表示用 formState を一致させ、再編集時に空白付きで復元されないようにする
 *
 * @param {{
 *   conditions: object[]
 *   elseEnabled: boolean
 *   elseValueType: string
 *   elseValue: string
 *   elseItemFileType?: string
 *   elseItemColumnName?: string
 * }} formData
 * @returns {object}
 */
function trimFormStateForStore(formData) {
  return {
    ...formData,
    conditions: (formData.conditions ?? []).map((row) => ({
      ...row,
      compareValue: String(row.compareValue ?? '').trim(),
      resultValue: String(row.resultValue ?? '').trim()
    })),
    elseValue: String(formData.elseValue ?? '').trim()
  }
}

/**
 * Validate single_condition form
 *
 * @param {{
 *   conditions: object[]
 *   elseEnabled: boolean
 *   elseValueType: string
 *   elseValue: string
 *   elseItemFileType?: string
 *   elseItemColumnName?: string
 * }} formData
 * @returns {Record<string, string[]> | null}
 */
export function validateSingleConditionForm(formData) {
  /** @type {Record<string, string[]>} */
  const nextErrors = {}
  // 複数条件と同様に、同一キーへ重複なく蓄積する（上書きしない）
  const add = (key, msg) => {
    if (!nextErrors[key]) nextErrors[key] = []
    if (!nextErrors[key].includes(msg)) nextErrors[key].push(msg)
  }

  if (!formData.conditions?.length) {
    add('general', ERRORS.AT_LEAST_ONE_CONDITION)
  } else {
    for (let i = 0; i < formData.conditions.length; i++) {
      const row = formData.conditions[i]
      // 各フィールド単位のキーにし、該当 input に赤枠(isError)を付けられるようにする
      if (!row.fileType?.trim()) add(`condition_${i}_fileType`, ERRORS.FILE_TYPE_REQUIRED)
      if (!row.columnName?.trim()) add(`condition_${i}_columnName`, ERRORS.COLUMN_NAME_REQUIRED)
      // 演算子は既定 '==' のため通常は未選択にならない
      if (!row.operator?.trim()) add(`condition_${i}_operator`, ERRORS.OPERATOR_REQUIRED)
      // 比較値
      if (row.compareValueType === 'item') {
        if (!row.compareItemFileType?.trim() || !row.compareItemColumnName?.trim()) {
          add(`condition_${i}_compareValue`, ERRORS.COMPARE_ITEM_REQUIRED)
        }
      } else if (row.compareValueType !== 'empty') {
        if (!String(row.compareValue ?? '').trim()) add(`condition_${i}_compareValue`, ERRORS.COMPARE_VALUE_REQUIRED)
        else if (row.compareValueType === 'date' && !isValidConditionMappingDateValue(row.compareValue)) {
          add(`condition_${i}_compareValue`, ERRORS.COMPARE_DATE_FORMAT)
        }
      }
      // 結果値
      if (row.resultType === 'item') {
        if (!row.resultItemFileType?.trim() || !row.resultItemColumnName?.trim()) {
          add(`condition_${i}_resultValue`, ERRORS.RESULT_ITEM_REQUIRED)
        }
      } else if (row.resultType !== 'empty') {
        if (!String(row.resultValue ?? '').trim()) add(`condition_${i}_resultValue`, ERRORS.RESULT_VALUE_REQUIRED)
        else if (row.resultType === 'date' && !isValidConditionMappingDateValue(row.resultValue)) {
          add(`condition_${i}_resultValue`, ERRORS.RESULT_DATE_FORMAT)
        }
      }
    }
  }

  // Else エラーは複数条件と同様に general へ集約し、フォーム下部にまとめて表示する（インライン表示にしない）
  // 比較値と同様に Else も単一キー(elseValue)へ集約し、種別を切り替えても赤枠が一貫するようにする
  if (formData.elseValueType === 'item') {
    if (!formData.elseItemFileType?.trim() || !formData.elseItemColumnName?.trim()) {
      add('elseValue', ERRORS.ELSE_ITEM_REQUIRED)
    }
  } else if (formData.elseValueType !== 'empty') {
    if (!String(formData.elseValue ?? '').trim()) add('elseValue', ERRORS.ELSE_VALUE_REQUIRED)
    else if (formData.elseValueType === 'date' && !isValidConditionMappingDateValue(formData.elseValue)) {
      add('elseValue', ERRORS.ELSE_DATE_FORMAT)
    }
  }

  return Object.keys(nextErrors).length > 0 ? nextErrors : null
}

/**
 * MappingInfo.SingleCondition から UI の conditions + else に変換（SR-172）
 *
 * @param {{ condition?: object; value?: object }[]} singleCondition
 * @param {import('../extra/types').FileTypeInfo[]} fileTypes
 * @returns {{
 *   conditions: object[]
 *   elseEnabled: boolean
 *   elseValueType: string
 *   elseValue: string
 *   elseItemFileType: string
 *   elseItemColumnName: string
 * }}
 */
function singleConditionToForm(singleCondition, fileTypes) {
  const conditions = []
  let elseEnabled = false
  let elseValueType = 'fixed'
  let elseValue = ''
  let elseItemFileType = ''
  let elseItemColumnName = ''

  const argToString = (a) => {
    if (a == null) return ''
    if (typeof a === 'string') return a
    if (typeof a === 'object') {
      if (a.type === 'Literal' || 'value' in a || 'Value' in a) {
        return String(a.value ?? a.Value ?? '')
      }
    }
    return ''
  }

  const parseCol = (node) => {
    if (!node || node.type !== 'CallExpression') return null
    const callee = node.callee
    const calleeName = callee && typeof callee === 'object' ? callee.name : undefined
    const legacyName = node.name
    if ((calleeName ?? legacyName) !== 'col') return null
    const args = node.arguments
    if (!Array.isArray(args) || args.length < 2) return null
    return { fileType: argToString(args[0]), columnName: argToString(args[1]) }
  }
  const parseLiteral = (node) => {
    if (!node || node.type !== 'Literal') return ''
    return String(node.value ?? '')
  }

  const list = Array.isArray(singleCondition) ? singleCondition : []
  for (const item of list) {
    const cond = storedStringToAst(item.conditionalExpression ?? item.ConditionalExpression ?? item.condition)
    const val = storedStringToAst(item.value ?? item.Value)
    if (cond?.type === 'Literal' && cond.value === true) {
      continue
    }
    if (cond?.type === 'BinaryExpression' && val) {
      const leftCol = parseCol(cond.left)
      const rightCol = parseCol(cond.right)
      const rightLit = cond.right?.type === 'Literal' ? parseLiteral(cond.right) : null
      const op = cond.operator || '=='

      let compareValueType = 'fixed'
      let compareValue = ''
      let compareItemFileType = ''
      let compareItemColumnName = ''

      if (rightCol) {
        compareValueType = 'item'
        compareItemFileType = rightCol.fileType
        compareItemColumnName = rightCol.columnName
      } else if (rightLit === '') {
        compareValueType = 'empty'
      } else if (rightLit !== null) {
        compareValueType = 'fixed'
        compareValue = rightLit
      }

      const resultCol = parseCol(val)
      const resultLit = val?.type === 'Literal' ? parseLiteral(val) : null

      let resultType = 'fixed'
      let resultValue = ''
      let resultItemFileType = ''
      let resultItemColumnName = ''

      if (resultCol) {
        resultType = 'item'
        resultItemFileType = resultCol.fileType
        resultItemColumnName = resultCol.columnName
      } else if (resultLit === '') {
        resultType = 'empty'
      } else if (resultLit !== null) {
        resultType = 'fixed'
        resultValue = resultLit
      }

      conditions.push({
        id: generateRowId(),
        fileType: leftCol?.fileType ?? '',
        columnName: leftCol?.columnName ?? '',
        operator: op,
        compareValueType,
        compareValue,
        compareItemFileType,
        compareItemColumnName,
        resultType,
        resultValue,
        resultItemFileType,
        resultItemColumnName
      })
    }
  }
  if (conditions.length === 0) {
    conditions.push(createEmptyConditionRow())
  }
  const lastItem = list[list.length - 1]
  if (lastItem && storedStringToAst(lastItem.conditionalExpression ?? lastItem.ConditionalExpression)?.value === true) {
    const val = storedStringToAst(lastItem.value ?? lastItem.Value)
    const resultCol = parseCol(val)
    const resultLit = val?.type === 'Literal' ? parseLiteral(val) : null
    elseEnabled = true

    if (resultCol) {
      elseValueType = 'item'
      elseItemFileType = resultCol.fileType
      elseItemColumnName = resultCol.columnName
      elseValue = ''
    } else if (resultLit === '') {
      elseValueType = 'empty'
      elseValue = ''
    } else {
      elseValueType = 'fixed'
      elseValue = resultLit ?? ''
    }
  }
  return {
    conditions,
    elseEnabled,
    elseValueType,
    elseValue,
    elseItemFileType,
    elseItemColumnName
  }
}

/** SingleConditionForm 本体（SR-167, SR-168, SR-170） */
export const SingleConditionForm = (props) => {
  const { fileTypes = [], formData, errors, onChange } = props

  const fileTypeOptions = useMemo(
    () => fileTypes.map((f) => ({ label: f.mappingFileType, value: f.mappingFileType })),
    [fileTypes]
  )
  const fileTypeOptionsWithKindLabel = useMemo(
    () => [{ label: '種別', value: '' }, ...fileTypeOptions],
    [fileTypeOptions]
  )
  const columnOptionsWithItemLabelByFileType = useMemo(() => {
    const m = new Map()
    fileTypes.forEach((f) => {
      const opts = (f?.headers ?? []).map((h) => ({ label: h, value: h }))
      m.set(f.mappingFileType, [{ label: '項目', value: '' }, ...opts])
    })
    m.set('', [{ label: '項目', value: '' }])
    return m
  }, [fileTypes])

  const updateCondition = useCallback(
    (index, patch) => {
      const next = formData.conditions.map((r, i) => (i === index ? { ...r, ...patch } : r))
      onChange({ ...formData, conditions: next }, () => ({}))
    },
    [formData, onChange]
  )

  const addCondition = useCallback(() => {
    onChange({ ...formData, conditions: [...formData.conditions, createEmptyConditionRow()] }, () => ({}))
  }, [formData, onChange])

  const removeCondition = useCallback(
    (index) => {
      const next = formData.conditions.filter((_, i) => i !== index)
      onChange(
        {
          ...formData,
          conditions: next.length ? next : [createEmptyConditionRow()]
        },
        () => ({})
      )
    },
    [formData, onChange]
  )

  const updateElse = useCallback(
    (patch) => {
      onChange(
        {
          ...formData,
          elseValueType: patch.elseValueType ?? formData.elseValueType ?? 'fixed',
          elseValue: patch.elseValue ?? formData.elseValue ?? '',
          elseItemFileType: patch.elseItemFileType ?? formData.elseItemFileType ?? '',
          elseItemColumnName: patch.elseItemColumnName ?? formData.elseItemColumnName ?? ''
        },
        () => ({})
      )
    },
    [formData, onChange]
  )

  const elseValueType = formData.elseValueType ?? 'fixed'
  const elseValue = formData.elseValue ?? ''
  const elseItemFileType = formData.elseItemFileType ?? ''
  const elseItemColumnName = formData.elseItemColumnName ?? ''

  return (
    <div className="text-h7">
      <div>
        {(formData.conditions ?? []).map((row, index) => (
          <div key={row.id} className="box-xy-bordered mb-4 rounded-xl p-4">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <span className="text-h7 font-bold blue1--text">条件 {index + 1}</span>
              {index > 0 && (
                <button
                  type="button"
                  data-testid={`single-condition-delete-${index}`}
                  className="inline-flex h-8 items-center rounded-md px-3 text-h7 font-medium white--text transition-opacity hover:opacity-90"
                  style={{ backgroundColor: '#f4392c' }}
                  onClick={() => removeCondition(index)}
                >
                  削除
                </button>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <ChrofyCustomSelect
                id={`single-condition-file-type-${index}`}
                name={`single-condition-file-type-${index}`}
                value={row.fileType}
                onChange={(e) => {
                  updateCondition(index, {
                    fileType: e.target.value ?? '',
                    columnName: '',
                    compareItemFileType: '',
                    compareItemColumnName: '',
                    resultItemFileType: '',
                    resultItemColumnName: ''
                  })
                }}
                className="w-[120px] shrink-0"
                selectClassName="!h-10 !min-h-10"
                options={fileTypeOptionsWithKindLabel}
                disabled={false}
                hasNoSelect={false}
                searchable
                isError={Boolean(errors?.[`condition_${index}_fileType`]?.length)}
                data-testid={`single-condition-file-type-${index}`}
              />
              <ChrofyCustomSelect
                id={`single-condition-column-name-${index}`}
                name={`single-condition-column-name-${index}`}
                value={row.columnName}
                onChange={(e) => updateCondition(index, { columnName: e.target.value ?? '' })}
                className={`w-[120px] shrink-0 ${!row.fileType ? 'opacity-60' : ''}`}
                selectClassName="!h-10 !min-h-10"
                options={columnOptionsWithItemLabelByFileType.get(row.fileType) ?? [{ label: '項目', value: '' }]}
                disabled={!row.fileType}
                hasNoSelect={false}
                searchable
                isError={Boolean(errors?.[`condition_${index}_columnName`]?.length)}
                data-testid={`single-condition-column-name-${index}`}
              />
              <ChrofyCustomSelect
                id={`single-condition-operator-${index}`}
                name={`single-condition-operator-${index}`}
                value={row.operator}
                onChange={(e) => updateCondition(index, { operator: e.target.value ?? '==' })}
                className=" "
                selectClassName="!h-10 !min-h-10"
                options={OPERATORS}
                disabled={false}
                hasNoSelect={false}
                isError={Boolean(errors?.[`condition_${index}_operator`]?.length)}
                data-testid={`single-condition-operator-${index}`}
              />
              <ChrofyCustomSelect
                id={`single-condition-compare-type-${index}`}
                name={`single-condition-compare-type-${index}`}
                value={row.compareValueType}
                onChange={(e) =>
                  updateCondition(index, {
                    compareValueType: e.target.value ?? 'fixed',
                    compareValue: '',
                    compareItemFileType: '',
                    compareItemColumnName: ''
                  })
                }
                className="w-[120px] shrink-0"
                selectClassName="!h-10 !min-h-10"
                options={COMPARE_VALUE_TYPES}
                disabled={false}
                hasNoSelect={false}
                data-testid={`single-condition-compare-type-${index}`}
              />
              {row.compareValueType === 'item' && (
                <>
                  <ChrofyCustomSelect
                    id={`single-condition-compare-item-file-${index}`}
                    name={`single-condition-compare-item-file-${index}`}
                    value={row.compareItemFileType}
                    onChange={(e) =>
                      updateCondition(index, {
                        compareItemFileType: e.target.value ?? '',
                        compareItemColumnName: ''
                      })
                    }
                    className="w-[120px] shrink-0"
                    selectClassName="!h-10 !min-h-10"
                    options={fileTypeOptionsWithKindLabel}
                    disabled={false}
                    hasNoSelect={false}
                    searchable
                    isError={Boolean(errors?.[`condition_${index}_compareValue`]?.length)}
                    data-testid={`single-condition-compare-item-file-${index}`}
                  />
                  <ChrofyCustomSelect
                    id={`single-condition-compare-item-column-${index}`}
                    name={`single-condition-compare-item-column-${index}`}
                    value={row.compareItemColumnName}
                    onChange={(e) => updateCondition(index, { compareItemColumnName: e.target.value ?? '' })}
                    className={`w-[120px] shrink-0 ${!row.compareItemFileType ? 'opacity-60' : ''}`}
                    selectClassName="!h-10 !min-h-10"
                    options={
                      columnOptionsWithItemLabelByFileType.get(row.compareItemFileType) ?? [
                        { label: '項目', value: '' }
                      ]
                    }
                    disabled={!row.compareItemFileType}
                    hasNoSelect={false}
                    searchable
                    isError={Boolean(errors?.[`condition_${index}_compareValue`]?.length)}
                    data-testid={`single-condition-compare-item-column-${index}`}
                  />
                </>
              )}
              {row.compareValueType !== 'item' && row.compareValueType !== 'empty' && (
                <div className="form-control w-[120px] shrink-0">
                  <input
                    type="text"
                    data-testid={`single-condition-compare-value-${index}`}
                    className={`h-10 min-h-10 w-full rounded-xl border-0 bg-white px-4 py-2 text-h7 ${
                      errors?.[`condition_${index}_compareValue`]?.length ? 'is-error' : ''
                    }`}
                    value={row.compareValue}
                    onChange={(e) => updateCondition(index, { compareValue: e.target.value ?? '' })}
                    placeholder={row.compareValueType === 'date' ? CONDITION_MAPPING_DATE_PLACEHOLDER : '値'}
                  />
                </div>
              )}
              <span className="px-1 text-h5 blue1--text">→</span>
              <ChrofyCustomSelect
                id={`single-condition-result-type-${index}`}
                name={`single-condition-result-type-${index}`}
                value={row.resultType}
                onChange={(e) =>
                  updateCondition(index, {
                    resultType: e.target.value ?? 'fixed',
                    resultValue: '',
                    resultItemFileType: '',
                    resultItemColumnName: ''
                  })
                }
                className="w-[120px] shrink-0"
                selectClassName="!h-10 !min-h-10"
                options={RESULT_VALUE_TYPES}
                disabled={false}
                hasNoSelect={false}
                data-testid={`single-condition-result-type-${index}`}
              />
              {row.resultType === 'item' && (
                <>
                  <ChrofyCustomSelect
                    id={`single-condition-result-item-file-${index}`}
                    name={`single-condition-result-item-file-${index}`}
                    value={row.resultItemFileType}
                    onChange={(e) =>
                      updateCondition(index, { resultItemFileType: e.target.value ?? '', resultItemColumnName: '' })
                    }
                    className="w-[120px] shrink-0"
                    selectClassName="!h-10 !min-h-10"
                    options={fileTypeOptionsWithKindLabel}
                    disabled={false}
                    hasNoSelect={false}
                    searchable
                    isError={Boolean(errors?.[`condition_${index}_resultValue`]?.length)}
                    data-testid={`single-condition-result-item-file-${index}`}
                  />
                  <ChrofyCustomSelect
                    id={`single-condition-result-item-column-${index}`}
                    name={`single-condition-result-item-column-${index}`}
                    value={row.resultItemColumnName}
                    onChange={(e) => updateCondition(index, { resultItemColumnName: e.target.value ?? '' })}
                    className={`w-[120px] shrink-0 ${!row.resultItemFileType ? 'opacity-60' : ''}`}
                    selectClassName="!h-10 !min-h-10"
                    options={
                      columnOptionsWithItemLabelByFileType.get(row.resultItemFileType) ?? [{ label: '項目', value: '' }]
                    }
                    disabled={!row.resultItemFileType}
                    hasNoSelect={false}
                    searchable
                    isError={Boolean(errors?.[`condition_${index}_resultValue`]?.length)}
                    data-testid={`single-condition-result-item-column-${index}`}
                  />
                </>
              )}
              {row.resultType !== 'item' && row.resultType !== 'empty' && (
                <div className="form-control w-[140px] shrink-0">
                  <input
                    type="text"
                    data-testid={`single-condition-result-value-${index}`}
                    className={`h-10 min-h-10 w-full rounded-xl border-0 bg-white px-4 py-2 text-h7 ${
                      errors?.[`condition_${index}_resultValue`]?.length ? 'is-error' : ''
                    }`}
                    value={row.resultValue}
                    onChange={(e) => updateCondition(index, { resultValue: e.target.value ?? '' })}
                    placeholder={row.resultType === 'date' ? CONDITION_MAPPING_DATE_PLACEHOLDER : '設定値'}
                  />
                </div>
              )}
            </div>
            <ChrofyShowErrors field={`condition_${index}_fileType`} errors={errors} />
            <ChrofyShowErrors field={`condition_${index}_columnName`} errors={errors} />
            <ChrofyShowErrors field={`condition_${index}_operator`} errors={errors} />
            <ChrofyShowErrors field={`condition_${index}_compareValue`} errors={errors} />
            <ChrofyShowErrors field={`condition_${index}_resultValue`} errors={errors} />
          </div>
        ))}
        <button
          type="button"
          className="mt-2 inline-flex h-10 items-center rounded-md px-4 text-h7 font-medium blue1--text transition-colors hover:bg-[#dbe5ff]"
          style={{
            backgroundColor: '#e8edf8',
            border: '1px solid rgba(49, 80, 165, 0.08)'
          }}
          onClick={addCondition}
          data-testid="single-condition-add"
        >
          ＋条件を追加
        </button>

        <div
          className="mt-4 flex flex-wrap items-center gap-2 rounded-xl border border-gray-300 bg-amber-50 p-4"
          data-testid="single-condition-else-block"
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-gray-300 bg-white">
            <svg className="h-5 w-5 grey--text" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </span>
          <span className="flex h-10 shrink-0 items-center text-h7 orange--text">条件を満たさない場合</span>
          <span className="shrink-0 px-1 text-h5 grey--text">→</span>
          <ChrofyCustomSelect
            id="single-condition-else-value-type"
            name="single-condition-else-value-type"
            value={elseValueType}
            onChange={(e) =>
              updateElse({
                elseValueType: e.target.value ?? 'fixed',
                elseValue: '',
                elseItemFileType: '',
                elseItemColumnName: ''
              })
            }
            className="w-[120px] shrink-0"
            selectClassName="!h-10 !min-h-10 rounded-lg border-gray-300 bg-white"
            options={RESULT_VALUE_TYPES}
            disabled={false}
            hasNoSelect={false}
            data-testid="single-condition-else-value-type"
          />
          {elseValueType === 'item' && (
            <>
              <ChrofyCustomSelect
                id="single-condition-else-item-file"
                name="single-condition-else-item-file"
                value={elseItemFileType}
                onChange={(e) =>
                  updateElse({
                    elseItemFileType: e.target.value ?? '',
                    elseItemColumnName: ''
                  })
                }
                className="w-[120px] shrink-0"
                selectClassName="!h-10 !min-h-10 rounded-lg border-gray-300 bg-white"
                options={fileTypeOptionsWithKindLabel}
                disabled={false}
                hasNoSelect={false}
                searchable
                isError={Boolean(errors?.elseValue?.length)}
                data-testid="single-condition-else-item-file"
              />
              <ChrofyCustomSelect
                id="single-condition-else-item-column"
                name="single-condition-else-item-column"
                value={elseItemColumnName}
                onChange={(e) => updateElse({ elseItemColumnName: e.target.value ?? '' })}
                className={`w-[120px] shrink-0 ${!elseItemFileType ? 'opacity-60' : ''}`}
                selectClassName="!h-10 !min-h-10 rounded-lg border-gray-300 bg-white"
                options={columnOptionsWithItemLabelByFileType.get(elseItemFileType) ?? [{ label: '項目', value: '' }]}
                disabled={!elseItemFileType}
                hasNoSelect={false}
                searchable
                isError={Boolean(errors?.elseValue?.length)}
                data-testid="single-condition-else-item-column"
              />
            </>
          )}
          {elseValueType !== 'item' && elseValueType !== 'empty' && (
            <div className="form-control min-w-[120px] flex-1 shrink-0">
              <input
                type="text"
                data-testid="single-condition-else-value"
                className={`text-input h-10 min-h-10 w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-h7 text-left${
                  errors?.elseValue?.length ? ' is-error' : ''
                }`}
                value={elseValue}
                onChange={(e) => updateElse({ elseValue: e.target.value ?? '' })}
                placeholder={elseValueType === 'date' ? CONDITION_MAPPING_DATE_PLACEHOLDER : 'デフォルト値'}
              />
            </div>
          )}
        </div>

        <ChrofyShowErrors field="elseValue" errors={errors} />
        <ChrofyShowErrors field="general" errors={errors} />
      </div>
    </div>
  )
}
